import { useEffect } from 'react';
import { X } from 'lucide-react';
import { ModalProps } from '../../types';

export function Modal({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true
}: ModalProps) {
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={handleBackdropClick}
                />

                {/* Modal */}
                <div
                    className={`relative bg-white rounded-xl shadow-2xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-hidden`}>

                    {/* Header with Title */}
                    {title && (
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                            {showCloseButton && (
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                    aria-label="Close modal"
                                >
                                    <X className="w-5 h-5"/>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Close Button (when no header) */}
                    {!title && showCloseButton && (
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors absolute top-4 right-4"
                            aria-label="Close modal"
                        >
                            <X className="w-5 h-5"/>
                        </button>
                    )}

          {/* Content */}
          <div className={`overflow-y-auto max-h-[calc(90vh-80px)] ${title ? 'p-6' : 'p-6'}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
